import { differenceInHours, formatDate } from "date-fns";

import { Box } from "@mui/material";

import PageContainer from "@components/PageContainer";

import { CampaignType } from "@interfaces/campaign.type";

import CampaignAlert from "@pages/Campaigns/components/CampaignAlert";
import CampaignChart from "@pages/Campaigns/components/CampaignChart";
import EngagementAndLeadCards from "@pages/Campaigns/components/EngagementAndLeadCards";
import GaugeMetrics from "@pages/Campaigns/components/GaugeMetrics";
import MetricCards from "@pages/Campaigns/components/MetricCards";

interface ICampaignPerformanceProperties {
  campaign: CampaignType;
  isOldCampaign: boolean;
}

function CampaignPerformance({ campaign, isOldCampaign }: ICampaignPerformanceProperties) {
  const clickedEmails = campaign.emails.filter((email) => email.linkClicked).length;
  const openedEmails = campaign.emails.filter((email) => email.opened).length;
  const repliedEmails = campaign.emails.filter((email) => email.replyReceived).length;

  const hoursDifference = differenceInHours(new Date(), new Date(campaign.created));

  if (isOldCampaign) {
    return (
      <Box sx={{ px: 4 }}>
        <CampaignAlert
          message="As of July 15, 2023, we began tracking Campaign performance metrics. Campaigns sent before this date do not have tracked metrics. We are working on updates to include these older campaigns. Stay tuned!"
          title="No Campaign Metrics"
        />
      </Box>
    );
  }

  if (campaign.scheduledAt) {
    const date = formatDate(new Date(campaign.scheduledAt), "MM/dd/yyyy, 'at' hh:mm a");
    return (
      <Box sx={{ px: 4 }}>
        <CampaignAlert
          message={
            <span>
              Your campaign will be sent on <b>{date}</b>. If you need to make changes, you can cancel the send at any
              time.
            </span>
          }
          severity="success"
          title="Campaign Scheduled"
        />
      </Box>
    );
  }

  return (
    <PageContainer
      sx={{
        gap: 4,
        paddingBottom: 4,
        px: 4,
      }}
    >
      {hoursDifference < 72 && (
        <CampaignAlert
          message={
            "Be patient while people get to their inbox. Additional Campaign metrics will be displayed once recipients begin interacting with your email."
          }
          title={"Awaiting Campaign Metrics"}
        />
      )}
      <MetricCards campaignId={campaign.id} emails={campaign.emails} />
      <GaugeMetrics emails={campaign.emails} />
      <EngagementAndLeadCards
        campaign={campaign}
        clickedEmails={clickedEmails}
        openedEmails={openedEmails}
        repliedEmails={repliedEmails}
      />
      <CampaignChart
        clickedEmails={clickedEmails}
        emails={campaign.emails}
        openedEmails={openedEmails}
        repliedEmails={repliedEmails}
      />
    </PageContainer>
  );
}

export default CampaignPerformance;
